import type { AxiosError, AxiosInstance } from 'axios';

import { openGuardrailsDialog } from '~/components/CmsMain/globals';

import { markGuardrailsHandled, parseGuardrailsError } from './guardrailsError';
import {
  accumulateKeys,
  buildOverrideHeaderValue,
  findOverrideHeader,
  mergeReasons,
  parseOverrideHeaderValue,
} from './overrideTokenHeader';
import { OVERRIDE_HEADER } from './types';

/**
 * Install the single generic seam that turns a `guardrails` 409/403 into the
 * interactive warn/block override flow (DR §6.8). No per-action wiring: every
 * guarded request routed through this axios instance is covered.
 *
 * Mechanism — the interceptor replays the failed request through the same instance,
 * so a fresh 409 (the next guarded event tripping, or a new warning during retry)
 * naturally re-enters this handler. Iterative state (accumulated `acknowledged_keys`
 * and merged override reason) rides in the `X-Guardrail-Override` header of the
 * request itself: each round reads the previous round's token off the failed
 * request's config and extends it — no external/persisted state, so nothing
 * survives a page reload. Each round stamps a fresh `tripped_at` when its 409 is
 * received (before the dialog opens), so the user's dwell time counts toward the
 * backend's minimum-override-delay check.
 *
 * On a block, Cancel, or any non-guardrails error, the original error is rejected
 * unchanged so existing `.catch` / `reportAxiosError` handling is untouched.
 */
export function installGuardrailsInterceptor(axiosInstance: AxiosInstance): number {
  return axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const body = parseGuardrailsError(error);
      if (!body) {
        return Promise.reject(error);
      }

      // Stamp when this round's 409 was received, before the user opens the dialog.
      const trippedAt = new Date().toISOString();

      const { proceed, reason, dialogShown } = await openGuardrailsDialog({
        violations: body.violations,
        isBlocked: body.is_blocked,
      });

      if (body.is_blocked || !proceed) {
        // A real block, or the user cancelled: surface the original error so the caller's
        // normal error handling runs. No replay. When the user actually saw the dialog, mark
        // the error so the central handler suppresses the redundant generic error popup — the
        // dialog already told them what happened.
        if (dialogShown) {
          markGuardrailsHandled(error);
        }
        return Promise.reject(error);
      }

      // Carry forward what earlier rounds already acknowledged (this request's own
      // override header, if it is itself a replay), then fold in this round. Read/clear the
      // prior header case-insensitively so a normalized-casing copy (axios may lowercase
      // header keys) is neither missed nor left behind as a duplicate on the replay.
      const config = error.config ?? {};
      const headers: Record<string, unknown> = { ...config.headers };
      const existing = findOverrideHeader(headers);
      const prior = parseOverrideHeaderValue(existing?.value);
      if (existing) {
        delete headers[existing.key];
      }

      const acknowledgedKeys = accumulateKeys(prior?.acknowledged_keys ?? [], body.violations);
      const mergedReason = mergeReasons(prior?.override_reason ?? '', reason);

      headers[OVERRIDE_HEADER] = buildOverrideHeaderValue({
        override_reason: mergedReason,
        acknowledged_keys: acknowledgedKeys,
        tripped_at: trippedAt,
        // The prior round's id wins over this round's body. The backend only *resumes* an id it
        // was handed back, so a round that trips a fresh rule answers with a newly minted one;
        // taking the body's would restart the chain mid-way and split one user intent into two
        // attempts — which is the miscount this field exists to remove.
        attempt_id: prior?.attempt_id ?? body.attempt_id,
      });

      // Replay the original request verbatim + the override header. A 2xx resolves
      // the caller's promise normally (it never sees the veto); a fresh guardrails
      // 409 re-enters this handler and re-prompts with the updated list.
      return axiosInstance({ ...config, headers });
    }
  );
}
